import "./style/App.css";
import "./style/Theme.css";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box } from "@mui/material";
import { lightTheme, darkTheme } from "./theme/Theme";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider, useDarkMode } from "./contexts/ThemeContext";
import { RoleProvider } from "./contexts/RoleContext";
import { AuthProvider } from "./contexts/AuthContext.js";
import { PendingUserProvider } from "./contexts/PendingUserContext.js";
import { DataCacheProvider } from "./contexts/DataCacheContext.js";
import { WorkspaceProvider } from "./contexts/WorkspaceContext.js";
import PrivateRoute from "./authentication/PrivateRoute";
import PublicRoute from "./authentication/PublicRoute";
import routing from "./routing";

// Components
import DynamicSideBar from "./components/SideBar/DynamicSideBar";
import DevModeBar from "./components/DevModeBar";
import Footer from "./components/Footer";
import RootRedirect from "./components/RootRedirect";

// Pages
import Login from "./pages/Login/Login";
import Profile from "./pages/Profile/Profile.js";
import Homepage from "./pages/HomePage";
import NotFound from "./pages/NotFound";
import NoPermission from "./pages/NoPermission";

// Donor pages
import DonateMaterials from "./pages/Donor/DonateMaterials.js";
import Workspace from "./pages/Donor/Workspace.js";
import Allocate from "./pages/Donor/Allocate.js";
import DonorInventory from "./pages/Donor/Inventory.js";
import DonorGHG from "./pages/Donor/GHG.js";

// Receiver pages
import MaterialManagement from "./pages/Receiver/MaterialManagement.js";
import Schedule from "./pages/Receiver/Schedule.js";
import ReceiverInventory from "./pages/Receiver/Inventory.js";
import FlowAnalysis from "./pages/Receiver/FlowAnalysis.js";
import GHG from "./pages/Receiver/GHG.js"

// Shared pages
import Partnerships from "./pages/Partnerships/Partnerships.js";

import "./i18n.js";

function AppContent() {
  const { darkMode } = useDarkMode();

  // Protected routes
  const protectedRoutes = [
    // Shared
    { path: routing.home.path, element: <Homepage /> },
    { path: "/profile", element: <Profile /> },
    { path: "/no-permission", element: <NoPermission /> },
    { path: routing.partnerships.path, element: <Partnerships /> },

    // Donor
    { path: routing.donor.donate_materials.path, element: <DonateMaterials /> },
    { path: routing.donor.workspace.path, element: <Workspace /> },
    { path: routing.donor.allocate.path, element: <Allocate /> },
    { path: routing.donor.inventory.path, element: <DonorInventory /> },
    { path: routing.donor.ghg.path, element: <DonorGHG /> },

    // Receiver
    {
      path: routing.receiver.material_management.path,
      element: <MaterialManagement />,
    },
    { path: routing.receiver.schedule.path, element: <Schedule /> },
    { path: routing.receiver.inventory.path, element: <ReceiverInventory /> },
    { path: routing.receiver.flow_analysis.path, element: <FlowAnalysis /> },
    { path: routing.receiver.ghg.path, element: <GHG />}
  ];

  return (
    <MuiThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* DevModeBar at the very top - shows on ALL pages, when demo mode is enabled */}
        <DevModeBar />

        {/* Main content area */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <Routes>
            {/* Root route - smart redirect based on auth status */}
            <Route path="/" element={<RootRedirect />} />

            {/* Public routes */}
            <Route element={<PublicRoute />}>
              <Route path={routing.login.path} element={<Login />} />
            </Route>

            {/* Protected routes with dynamic sidebar */}
            <Route element={<PrivateRoute />}>
              <Route element={<DynamicSideBar />}>
                {protectedRoutes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={route.element}
                  />
                ))}
              </Route>
            </Route>

            {/* 404 fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>

        {/* Footer at the bottom - shows on ALL pages */}
        <Footer />
      </Box>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <RoleProvider>
          <AuthProvider>
            <PendingUserProvider>
              <DataCacheProvider>
                <WorkspaceProvider>
                  <AppContent />
                </WorkspaceProvider>
              </DataCacheProvider>
            </PendingUserProvider>
          </AuthProvider>
        </RoleProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
